import type { SupabaseClient } from "@supabase/supabase-js";
import type { LibraryEntry, WatchProgress } from "./types";

export interface PullPage<T> {
  records: T[];
  cursor: string | null;
}

/** Backend de sincronizacion. Misma interfaz que RemoteLibraryDataSource en Android. */
export interface RemoteLibrary {
  pushLibrary(records: LibraryEntry[]): Promise<void>;
  pullLibrary(since: string | null): Promise<PullPage<LibraryEntry>>;
  pushProgress(records: WatchProgress[]): Promise<void>;
  pullProgress(since: string | null): Promise<PullPage<WatchProgress>>;
}

// --- Mapeo camelCase <-> columnas de supabase/migrations ---

interface LibraryRow {
  anime_url: string;
  provider: string;
  title: string;
  image_url: string;
  is_favorite: boolean;
  is_watching: boolean;
  is_finished: boolean;
  is_watch_later: boolean;
  updated_at: number;
  deleted_at: number | null;
  server_updated_at?: string;
}

interface ProgressRow {
  episode_url: string;
  anime_url: string;
  anime_title: string;
  episode_title: string;
  image_url: string;
  position_ms: number;
  duration_ms: number;
  updated_at: number;
  deleted_at: number | null;
  server_updated_at?: string;
}

export const toLibraryRow = (e: LibraryEntry): LibraryRow => ({
  anime_url: e.animeUrl,
  provider: e.provider,
  title: e.title,
  image_url: e.imageUrl,
  is_favorite: e.isFavorite,
  is_watching: e.isWatching,
  is_finished: e.isFinished,
  is_watch_later: e.isWatchLater,
  updated_at: e.updatedAt,
  deleted_at: e.deletedAt,
});

export const fromLibraryRow = (r: LibraryRow): LibraryEntry => ({
  animeUrl: r.anime_url,
  provider: r.provider,
  title: r.title,
  imageUrl: r.image_url,
  isFavorite: r.is_favorite,
  isWatching: r.is_watching,
  isFinished: r.is_finished,
  isWatchLater: r.is_watch_later,
  updatedAt: Number(r.updated_at),
  deletedAt: r.deleted_at === null ? null : Number(r.deleted_at),
});

export const toProgressRow = (p: WatchProgress): ProgressRow => ({
  episode_url: p.episodeUrl,
  anime_url: p.animeUrl,
  anime_title: p.animeTitle,
  episode_title: p.episodeTitle,
  image_url: p.imageUrl,
  position_ms: Math.round(p.positionMs),
  duration_ms: Math.round(p.durationMs),
  updated_at: p.updatedAt,
  deleted_at: p.deletedAt,
});

export const fromProgressRow = (r: ProgressRow): WatchProgress => ({
  episodeUrl: r.episode_url,
  animeUrl: r.anime_url,
  animeTitle: r.anime_title,
  episodeTitle: r.episode_title,
  imageUrl: r.image_url,
  positionMs: Number(r.position_ms),
  durationMs: Number(r.duration_ms),
  updatedAt: Number(r.updated_at),
  deletedAt: r.deleted_at === null ? null : Number(r.deleted_at),
});

const PAGE_SIZE = 500;

/**
 * Adaptador Supabase. Las politicas RLS limitan cada consulta a las filas del usuario, y el
 * trigger del servidor descarta escrituras mas antiguas (last-write-wins).
 */
export function createSupabaseRemote(client: SupabaseClient): RemoteLibrary {
  async function pull<Row extends { server_updated_at?: string }, T>(table: string, since: string | null, map: (row: Row) => T): Promise<PullPage<T>> {
    let query = client.from(table).select("*").order("server_updated_at", { ascending: true }).limit(PAGE_SIZE);
    if (since) query = query.gt("server_updated_at", since);
    const { data, error } = await query;
    if (error) throw error;
    const rows = (data ?? []) as Row[];
    return { records: rows.map(map), cursor: rows.at(-1)?.server_updated_at ?? since };
  }

  return {
    async pushLibrary(records) {
      if (records.length === 0) return;
      const { error } = await client.from("library_entries").upsert(records.map(toLibraryRow), { onConflict: "user_id,anime_url" });
      if (error) throw error;
    },
    pullLibrary: (since) => pull<LibraryRow, LibraryEntry>("library_entries", since, fromLibraryRow),
    async pushProgress(records) {
      if (records.length === 0) return;
      const { error } = await client.from("watch_progress").upsert(records.map(toProgressRow), { onConflict: "user_id,episode_url" });
      if (error) throw error;
    },
    pullProgress: (since) => pull<ProgressRow, WatchProgress>("watch_progress", since, fromProgressRow),
  };
}
